<template>
  <o-dialog
    ref="dialog"
    :title="title"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: scheduled ? $t('general.schedule') : $t('general.send'), icon: 'send', type: 'confirm', variant: 'success', disabled: isDisabled, callback: confirm },
    ]"
    :portal="portal"
  >
    <o-alert v-if="credits && isCampaignTypeSMS && isTypeMessages" show variant="warning" class="text-left mb-4">
      <p class="text-sm" v-html="$t('components.campaign.actions.send.messagesAvailable', { count: credits.messageCount, balance: credits.currentMessageBalance })" />
      <p v-if="!credits.canSend" class="text-sm mt-3">
        {{ $t('components.campaign.actions.send.insufficientMessages') }}
        <o-button to="/manage/communications/sms" variant="primary" target="_blank" class="mt-3">
          {{ $t('general.purchase') }}
        </o-button>
      </p>
    </o-alert>

    <o-alert v-if="credits && isCampaignTypeSMS && isTypeCredits" show variant="warning" class="text-left mb-4">
      <p class="text-sm" v-html="$t('components.campaign.actions.send.creditsAvailable', { approximate: credits.approximateCredits, available: credits.availableCredits })" />
      <p v-if="!credits.canSend" class="text-sm mt-3">
        {{ $t('components.campaign.actions.send.insufficientCredit') }}
        <o-button to="/manage/communications/sms" variant="primary" target="_blank" class="mt-3">
          {{ $t('general.purchase') }}
        </o-button>
      </p>
    </o-alert>

    <o-field-group :label="$t('components.campaign.actions.send.whenSend')">
      <segment-field
        v-model="scheduled"
        :options="[
          { label: $t('general.now'), value: false },
          { label: $t('components.campaign.actions.send.later'), value: true },
        ]"
      />
    </o-field-group>

    <o-field-group v-if="scheduled" :label="$t('components.campaign.actions.send.scheduled')" date class="mt-5 block">
      <date-field
        v-model="scheduledDate"
        mode="dateTime"
        position-fixed
        :timezone="$auth.info.location.timezoneUnix"
        :augmenter="Augmenters.timestamp"
        :min-date="new Date()"
      />
    </o-field-group>

    <!--<o-alert v-if="scheduled" show variant="info" class="text-left mt-6">
      <p class="text-sm mb-2">
        The final list of customers to receive this campaign will be determined at the scheduled date/time.
      </p>
      <p class="text-sm">
        The scheduled date/time is not exact. The campaign will be sent as soon as possible after the scheduled date/time.
      </p>
    </o-alert>-->
  </o-dialog>
</template>

<script setup lang="ts">
import { OAlert, OFieldGroup } from '@/components/elements'
import { SegmentField, DateField } from '@/components/fields'

import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'

import type { Campaign } from '~/_api/models/campaign'

interface Props {
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
  (e: 'error'): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $i18n, $notification, $bus } = useNuxtApp()

const dialog = ref<any>()
const campaign = ref<Campaign | null>(null)
const scheduled = ref(false)
const scheduledDate = ref<string | null>(null)
const credits = ref<any>(null)

const title = computed(() => {
  return isCampaignTypeEmail.value ? $i18n.t('components.campaign.actions.send.titleEmail') : $i18n.t('components.campaign.actions.send.titleSMS')
})

const info = computed(() => {
  return isCampaignTypeEmail.value ? $i18n.t('components.campaign.actions.send.infoEmail', { count: customerCount.value }) : $i18n.t('components.campaign.actions.send.infoSMS', { count: customerCount.value })
})

const toastInfo = computed(() => {
  return isCampaignTypeEmail.value ? $i18n.t('components.campaign.actions.send.infoEmailToast', { count: customerCount.value }) : $i18n.t('components.campaign.actions.send.infoSMSToast', { count: customerCount.value })
})

const isCampaignTypeEmail = computed(() => {
  return campaign.value?.isCampaignTypeEmail
})

const isCampaignTypeSMS = computed(() => {
  return campaign.value?.isCampaignTypeSMS
})

const isTypeMessages = computed(() => {
  return credits.value?.type === 'messages'
})

const isTypeCredits = computed(() => {
  return credits.value?.type === 'credits'
})

const customerCount = computed(() => {
  if (Array.isArray(campaign.value?.customerIDs)) {
    return campaign.value.customerIDs.length
  }

  return Number(campaign.value?.customerCount || 0)
})

const hasCustomers = computed(() => {
  return customerCount.value > 0
})

const isDisabled = computed(() => {
  return !hasCustomers.value ||
    (scheduled.value && !scheduledDate.value) ||
    (isCampaignTypeSMS.value && (!credits.value || !credits.value.canSend))
})

const calculateCredits = async () => {
  if (!campaign.value) return

  try {
    const response = await $api.campaign.calculateCredits(campaign.value)

    if (response.data) {
      credits.value = response.data
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ title: $i18n.t('components.campaign.actions.send.error'), errors: e?.errors })
  }
}

const open = (model: Campaign, isScheduled = false) => {
  campaign.value = model
  scheduled.value = isScheduled
  scheduledDate.value = null
  credits.value = null

  if (isCampaignTypeSMS.value) {
    calculateCredits()
  }

  dialog.value.open()
}

const confirm = async () => {
  if (!campaign.value) return

  if (!hasCustomers.value) {
    $notification.error({ errors: ['No customers'] })
    return false
  }

  if (scheduled.value && !scheduledDate.value) {
    $notification.error({ errors: [$i18n.t('general.validationError')] })
    return false
  }

  emit('update:loading', true)

  try {
    let response

    if (scheduled.value) {
      response = await $api.campaign.send(campaign.value, { scheduledDate: scheduledDate.value })
    } else {
      response = await $api.campaign.send(campaign.value)
    }

    if (response.success) {
      $notification.toast({
        title: $i18n.t('general.sent'),
        text: toastInfo.value,
      })

      emit('success')
      $bus.emit.updated('Campaign', campaign.value)

      return true
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })

    emit('error')

    console.log(e)
  } finally {
    emit('update:loading', false)
  }
}

defineExpose({ open })
</script>
